import { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as SendSolidIcon } from 'resources/images/send-solid.svg';
import { useForm } from 'react-hook-form';
import { Flex, IconButton } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { VirtuosoHandle } from 'react-virtuoso';
import { ChatInputProps, CreateChatMessageValidation } from 'types/chat.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { addToMessages } from 'app/store/Chat/slice';
import { createChatMessageEffect } from 'app/store/Chat/effects';
import { getCurrentProfile } from 'app/store/Profile/selectors';
import { v4 as generateUuid } from 'uuid';
import { EditableContentField } from 'app/components/Core/EditableContentField/EditableContentField';
import { getIsChatWindowOpened } from 'app/store/Chat/selectors';
import { getNearLimitMessage } from './utils';
import { getMessageFormValidationSchema } from './validation';
import { createRetryMessageTemplate, scrollToIndex } from '../utils';

interface MessageFormData {
  text: string;
}

interface MessageFormProps {
  messageInputProps?: ChatInputProps;
  channelGuid?: string;
  validation: CreateChatMessageValidation;
  virtuosoRef: RefObject<VirtuosoHandle>;
  disabled?: boolean;
}

export const MessageForm: FC<MessageFormProps> = ({
  messageInputProps,
  channelGuid,
  validation,
  virtuosoRef,
  disabled,
}) => {
  const dispatch = useAppDispatch();
  const userProfileCard = useAppSelector(getCurrentProfile);
  const isChatOpen = useAppSelector(getIsChatWindowOpened);
  const textFieldRef = useRef<HTMLDivElement>(null);
  const [isFirstMessage, setIsFirstMessage] = useState(true);

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isSubmitting, errors },
  } = useForm<MessageFormData>({
    defaultValues: { text: '' },
    mode: 'all',
    resolver: yupResolver(getMessageFormValidationSchema(validation)),
  });
  const watchText = watch('text');
  const nearLimitMessage = useMemo(
    () => getNearLimitMessage(watchText, validation),
    [watchText, validation]
  );

  const resetInput = () => {
    reset();
    if (textFieldRef.current) {
      textFieldRef.current.replaceChildren();
    }
  };

  const onSubmit = handleSubmit(async (data: MessageFormData) => {
    if (!data.text.trim().length) {
      resetInput();
      return;
    }
    try {
      if (channelGuid) {
        const refId = generateUuid();
        await dispatch(
          createChatMessageEffect({
            channelGuid,
            message: data.text,
            refId,
            isFirstMessage,
          })
        ).unwrap();
        setIsFirstMessage(false);
      }
    } catch (e) {
      if (userProfileCard) {
        dispatch(
          addToMessages(createRetryMessageTemplate(data.text, userProfileCard))
        );
      }
    } finally {
      scrollToIndex(virtuosoRef, Number.MAX_SAFE_INTEGER);
      resetInput();
    }
  });

  useEffect(() => {
    if (!isChatOpen) {
      setIsFirstMessage(true);
    }
  }, [isChatOpen]);

  return (
    <Flex as="form" gap={2} mt={2} alignItems="flex-end" onSubmit={onSubmit}>
      <EditableContentField
        isInvalid={errors.text?.message ? Boolean(errors.text) : false}
        errorMessage={errors.text?.message}
        helperText={nearLimitMessage}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter' && !evt.shiftKey && !isSubmitting) {
            evt.preventDefault();
            onSubmit();
          }
        }}
        onPaste={(event) => {
          event.preventDefault();

          const prevHeight = textFieldRef.current?.scrollHeight;
          const paste = event.clipboardData.getData('text');

          const selection = window.getSelection();
          if (!selection?.rangeCount) return;

          selection.deleteFromDocument();
          selection.getRangeAt(0).insertNode(document.createTextNode(paste));
          selection.collapseToEnd();

          const afterHeight = textFieldRef.current?.scrollHeight;

          if (textFieldRef.current) {
            textFieldRef.current.scrollBy({
              top: Number(afterHeight) - Number(prevHeight) || 0,
              behavior: 'auto',
            });
            setValue('text', textFieldRef.current.innerText, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }}
        onInput={(e) => {
          setValue('text', e.currentTarget.innerText, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        isDisabled={disabled}
        {...messageInputProps}
        ref={textFieldRef}
      />
      <IconButton
        type="submit"
        data-testid="messenger-send-button"
        size="md"
        rounded="full"
        bg="M.70"
        color="M.10"
        icon={<SendSolidIcon width={16} height={16} />}
        aria-label="send message"
        _disabled={{
          cursor: 'default',
          opacity: 0.36,
        }}
        isDisabled={!isValid || isSubmitting}
      />
    </Flex>
  );
};
